/**
 * CEO Command Center - Domain Management Components
 * 
 * UI components for managing business domains:
 * - DomainEditor: Add/edit domain modal
 * - DomainList: List of domains with actions
 * - DomainCard: Individual domain display
 * 
 * @module components/features/domains
 * @version 10.2.0
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../../utils/format';
import { Button, Card, Modal, Input, Select, Badge, Textarea } from '../../ui';
import { useDomainStore, type DomainConfig } from '../../../stores/domainStore';

// ============================================
// ICON PICKER
// ============================================

const DOMAIN_ICONS = [
  '👥', '🔧', '📈', '⚙️', '💵', '🎯', '💼', '📊', '🚀', '💡',
  '🔬', '📱', '🌐', '🛡️', '📦', '🎨', '📣', '🤝', '📋', '⚡',
];

const DOMAIN_COLORS = [
  { id: 'purple', class: 'bg-purple-500', label: 'Purple' },
  { id: 'blue', class: 'bg-blue-500', label: 'Blue' },
  { id: 'green', class: 'bg-green-500', label: 'Green' },
  { id: 'amber', class: 'bg-amber-500', label: 'Amber' },
  { id: 'emerald', class: 'bg-emerald-500', label: 'Emerald' },
  { id: 'red', class: 'bg-red-500', label: 'Red' },
  { id: 'pink', class: 'bg-pink-500', label: 'Pink' },
  { id: 'indigo', class: 'bg-indigo-500', label: 'Indigo' },
  { id: 'cyan', class: 'bg-cyan-500', label: 'Cyan' },
  { id: 'orange', class: 'bg-orange-500', label: 'Orange' },
];

// ============================================
// DOMAIN EDITOR MODAL
// ============================================

interface DomainEditorProps {
  domain?: DomainConfig | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<DomainConfig, 'id' | 'createdAt' | 'updatedAt' | 'order'>) => void;
}

export const DomainEditor: React.FC<DomainEditorProps> = ({
  domain,
  isOpen,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [fullName, setFullName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('📋');
  const [color, setColor] = useState('bg-blue-500');
  const [uploadLabel, setUploadLabel] = useState('');
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName(domain?.name || '');
      setFullName(domain?.fullName || '');
      setDescription(domain?.description || '');
      setIcon(domain?.icon || '📋');
      setColor(domain?.color || 'bg-blue-500');
      setUploadLabel(domain?.uploadLabel || '');
    }
  }, [domain, isOpen]);
  
  const handleSubmit = () => {
    if (!name.trim()) return;
    
    onSave({
      name: name.trim(),
      fullName: fullName.trim() || name.trim(),
      description: description.trim(),
      icon,
      color,
      uploadLabel: uploadLabel.trim() || `Upload ${name} Notes`,
      isDefault: domain?.isDefault || false,
      isActive: domain?.isActive ?? true,
    });
    
    onClose();
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={domain ? 'Edit Domain' : 'Add Domain'}
      size="md"
    >
      <div className="space-y-4">
        {/* Name */}
        <Input
          label="Short Name"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g., HR, Legal, Marketing"
          required
          disabled={domain?.isDefault}
        />
        
        {/* Full Name */}
        <Input
          label="Full Name"
          value={fullName}
          onChange={e => setFullName(e.target.value)}
          placeholder="e.g., Human Resources, Legal & Compliance"
        />
        
        {/* Description */}
        <Textarea
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Brief description of this domain..."
          rows={2}
        />
        
        {/* Icon Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Icon
          </label>
          <div className="flex flex-wrap gap-2">
            {DOMAIN_ICONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => setIcon(emoji)}
                className={cn(
                  'w-10 h-10 text-xl rounded-lg border-2 transition-all',
                  icon === emoji
                    ? 'border-coral-500 bg-coral-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
        
        {/* Color Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Color
          </label>
          <div className="flex flex-wrap gap-2">
            {DOMAIN_COLORS.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => setColor(c.class)}
                className={cn(
                  'w-10 h-10 rounded-lg border-2 transition-all',
                  c.class,
                  color === c.class
                    ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-400'
                    : 'border-transparent hover:scale-110'
                )}
                title={c.label}
              />
            ))}
          </div>
        </div>
        
        {/* Upload Label */}
        <Input
          label="Upload Button Label"
          value={uploadLabel}
          onChange={e => setUploadLabel(e.target.value)}
          placeholder="e.g., Upload Meeting Notes"
        />
        
        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!name.trim()}
          >
            {domain ? 'Save Changes' : 'Add Domain'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ============================================
// DOMAIN CARD
// ============================================

interface DomainCardProps {
  domain: DomainConfig;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  taskCount?: number;
}

export const DomainCard: React.FC<DomainCardProps> = ({
  domain,
  onEdit,
  onDelete,
  onToggle,
  taskCount = 0,
}) => {
  return (
    <div className={cn(
      'p-4 border rounded-lg transition-all',
      domain.isActive ? 'bg-white' : 'bg-gray-50 opacity-60'
    )}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg',
            domain.color
          )}>
            {domain.icon}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{domain.name}</h3>
              {domain.isDefault && (
                <Badge className="bg-gray-100 text-gray-600 text-[10px]">Default</Badge>
              )}
              {!domain.isActive && (
                <Badge className="bg-red-100 text-red-600 text-[10px]">Hidden</Badge>
              )}
            </div>
            <p className="text-sm text-gray-500">{domain.fullName}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={onToggle}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
            title={domain.isActive ? 'Hide from sidebar' : 'Show in sidebar'}
          >
            {domain.isActive ? '👁️' : '👁️‍🗨️'}
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
          >
            ✏️
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded"
            title={domain.isDefault ? 'Hide domain' : 'Delete domain'}
          >
            🗑️
          </button>
        </div>
      </div>
      
      {domain.description && (
        <p className="text-sm text-gray-500 mt-2 ml-13">{domain.description}</p>
      )}
      
      <div className="flex items-center gap-4 mt-3 ml-13 text-xs text-gray-400">
        <span>{taskCount} tasks</span>
      </div>
    </div>
  );
};

// ============================================
// DOMAIN MANAGER PANEL
// ============================================

interface DomainManagerProps {
  taskCounts?: Record<string, number>;
  className?: string;
}

export const DomainManager: React.FC<DomainManagerProps> = ({
  taskCounts = {},
  className,
}) => {
  const { domains, addDomain, updateDomain, deleteDomain, toggleDomainActive } = useDomainStore();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState<DomainConfig | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  
  const sortedDomains = [...domains].sort((a, b) => a.order - b.order);
  
  const handleEdit = (domain: DomainConfig) => {
    setEditingDomain(domain);
    setEditorOpen(true);
  };
  
  const handleSave = (data: Omit<DomainConfig, 'id' | 'createdAt' | 'updatedAt' | 'order'>) => {
    if (editingDomain) {
      updateDomain(editingDomain.id, data);
    } else {
      addDomain(data);
    }
    setEditingDomain(null);
  };
  
  const handleDelete = (id: string) => {
    deleteDomain(id);
    setDeleteConfirm(null);
  };
  
  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Business Domains</h2>
          <p className="text-sm text-gray-500">
            {domains.length} domains • Manage your business areas
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditorOpen(true)}>
          ➕ Add Domain
        </Button>
      </div>
      
      {/* Domains List */}
      <div className="space-y-3">
        {sortedDomains.map(domain => (
          <DomainCard
            key={domain.id}
            domain={domain}
            onEdit={() => handleEdit(domain)}
            onDelete={() => setDeleteConfirm(domain.id)}
            onToggle={() => toggleDomainActive(domain.id)}
            taskCount={taskCounts[domain.id] || 0}
          />
        ))}
      </div>
      
      {/* Editor Modal */}
      <DomainEditor
        domain={editingDomain}
        isOpen={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingDomain(null);
        }}
        onSave={handleSave}
      />
      
      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Domain"
        size="sm"
      >
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete this domain? Tasks in this domain will not be deleted but will need to be reassigned.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="bg-red-500 hover:bg-red-600"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

// ============================================
// EXPORTS
// ============================================

export default {
  DomainEditor,
  DomainCard,
  DomainManager,
};
